import { CatalogSuggestion } from '../../../redux/api/fymAPI.types';
import { CatalogClaimSnapshot } from './types';

/** What the claim step needs out of the musicapi `tracks.details` answer. */
export interface ClaimTrackDetails {
  mainName: string;
  mainArtistName: string | null;
  album: { albumType: string | null; releaseDate: string | null } | null;
  /** Null when the catalog track has no image. */
  imageUrl: string | null;
  serviceLinks: { integrationType: string; url: string }[];
}

/** Only a full day survives: the catalog also stores bare years, which the claim route refuses. */
const FULL_DAY = /^\d{4}-\d{2}-\d{2}$/;

function toIsoDay(releaseDate: string | null | undefined): string | undefined {
  if (releaseDate === null || releaseDate === undefined || !FULL_DAY.test(releaseDate)) {
    return undefined;
  }
  const parsed = new Date(`${releaseDate}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The snapshot stored with the claim.
 *
 * It is a copy on purpose: the catalog track can be re-indexed, merged or
 * renamed later, and the row the artist claimed has to keep saying what they
 * claimed. `fallbackArtistName` covers a credit musicapi holds no artist for —
 * the account's own name is a better answer than an empty one.
 */
export function buildClaimSnapshot(
  catalogTrackMaid: string,
  track: ClaimTrackDetails,
  fallbackArtistName: string
): CatalogClaimSnapshot {
  const serviceLinks = track.serviceLinks.reduce<Record<string, string>>((links, link) => {
    // eslint-disable-next-line no-param-reassign
    links[link.integrationType] = link.url;
    return links;
  }, {});

  return {
    catalogTrackMaid,
    title: track.mainName,
    artistName: track.mainArtistName ?? fallbackArtistName,
    releaseType:
      track.album?.albumType === null || track.album?.albumType === undefined
        ? undefined
        : capitalize(track.album.albumType),
    releaseDate: toIsoDay(track.album?.releaseDate),
    artworkUrl: track.imageUrl ?? undefined,
    serviceLinks: Object.keys(serviceLinks).length === 0 ? undefined : serviceLinks,
  };
}

/**
 * The same snapshot, built out of a profile suggestion.
 *
 * A profile suggestion has no catalog track behind it — its key is a synthetic
 * `profile:…` id — so there is nothing to read by maid. Everything the claim
 * stores already travels on the row, and the nulls become absent keys because
 * the claim body takes optional fields, not null ones.
 */
export function buildProfileClaimSnapshot(suggestion: CatalogSuggestion): CatalogClaimSnapshot {
  return {
    catalogTrackMaid: suggestion.catalogTrackMaid,
    title: suggestion.title,
    artistName: suggestion.artistName,
    releaseType: suggestion.releaseType === null ? undefined : capitalize(suggestion.releaseType),
    releaseDate: toIsoDay(suggestion.releaseDate),
    artworkUrl: suggestion.artworkUrl ?? undefined,
    serviceLinks: suggestion.serviceLinks ?? undefined,
  };
}
